import * as React from 'react';
import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import type {
  ClientConversionPayload,
  DedupeSuggestion,
  ProspectStage,
  SalesActivityKind,
  SalesActivityOutcome,
} from '@bits/shared/zod';
import { VALID_TRANSITIONS, formatInr, paise, rupeesToPaise } from '@bits/domain';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { stageBadgeVariant, stageLabel } from './sales.prospects';

export const salesProspectDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sales/prospects/$prospectId',
  component: SalesProspectDetailPage,
});

const CORRECTABLE_STAGES: readonly ProspectStage[] = [
  'lead',
  'contacted',
  'proposal_sent',
  'negotiation',
];

const ACTIVITY_KINDS: readonly SalesActivityKind[] = [
  'call',
  'meeting',
  'demo',
  'proposal',
  'follow_up',
  'email',
  'other',
];

const OUTCOMES: readonly SalesActivityOutcome[] = ['positive', 'neutral', 'negative', 'blocked'];

type EditValues = {
  displayName: string;
  ownerId: string;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  estimatedValueRupees: string;
  expectedCloseDate: string;
  nextTouchDate: string;
  notes: string;
  serviceOfferingIds: string[];
};

type ActivityValues = {
  kind: SalesActivityKind;
  occurredAt: string;
  durationMinutes: string;
  summary: string;
  outcome: SalesActivityOutcome | '';
};

type ConversionValues = {
  legalName: string;
  gstin: string;
  gstTreatment: ClientConversionPayload['gstTreatment'];
  pan: string;
  billingAddress: string;
  placeOfSupplyState: string;
  paymentTermsDays: number;
  force: boolean;
};

type LostValues = { lostReason: string };
type CorrectValues = { toStage: ProspectStage; reason: string };

function nowLocalIso(): string {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60 * 1000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
}

function SalesProspectDetailPage(): JSX.Element {
  const { prospectId } = salesProspectDetailRoute.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | null>(null);
  const [conversionOpen, setConversionOpen] = React.useState(false);
  const [lostOpen, setLostOpen] = React.useState(false);
  const [conversionSuggestions, setConversionSuggestions] = React.useState<DedupeSuggestion[]>([]);
  const [pendingStage, setPendingStage] = React.useState<ProspectStage | null>(null);
  const [correctOpen, setCorrectOpen] = React.useState(false);

  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me(), retry: false });
  const isAdmin = React.useMemo(
    () => (meQuery.data?.person.roles ?? []).includes('Admin'),
    [meQuery.data],
  );

  const detailQuery = useQuery({
    queryKey: ['sales-prospect', prospectId],
    queryFn: () => api.getProspect(prospectId),
  });
  const activitiesQuery = useQuery({
    queryKey: ['sales-prospect', prospectId, 'activities'],
    queryFn: () => api.listProspectActivities(prospectId),
  });
  const peopleQuery = useQuery({ queryKey: ['people-simple'], queryFn: () => api.listPeopleSimple() });
  const servicesQuery = useQuery({
    queryKey: ['sales-services'],
    queryFn: () => api.listServiceOfferings(),
  });

  const editForm = useForm<EditValues>({
    defaultValues: {
      displayName: '',
      ownerId: '',
      primaryContactName: '',
      primaryContactEmail: '',
      primaryContactPhone: '',
      estimatedValueRupees: '',
      expectedCloseDate: '',
      nextTouchDate: '',
      notes: '',
      serviceOfferingIds: [],
    },
  });

  React.useEffect(() => {
    const d = detailQuery.data;
    if (!d) return;
    editForm.reset({
      displayName: d.displayName,
      ownerId: d.ownerId,
      primaryContactName: d.primaryContactName ?? '',
      primaryContactEmail: d.primaryContactEmail ?? '',
      primaryContactPhone: d.primaryContactPhone ?? '',
      estimatedValueRupees:
        d.estimatedValuePaise !== null
          ? (Number(d.estimatedValuePaise) / 100).toFixed(2)
          : '',
      expectedCloseDate: d.expectedCloseDate ?? '',
      nextTouchDate: d.nextTouchDate ?? '',
      notes: d.notes ?? '',
      serviceOfferingIds: d.interests.map((i) => i.id),
    });
  }, [detailQuery.data, editForm]);

  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['sales-prospect', prospectId] }),
      queryClient.invalidateQueries({ queryKey: ['sales-prospect', prospectId, 'activities'] }),
      queryClient.invalidateQueries({ queryKey: ['sales-prospects'] }),
    ]);
  };

  const updateMutation = useMutation({
    mutationFn: (v: EditValues) => {
      const estRupees = v.estimatedValueRupees.trim();
      const estPaise =
        estRupees === '' ? null : Number(rupeesToPaise(Number(estRupees)));
      return api.updateProspect(prospectId, {
        displayName: v.displayName.trim(),
        ownerId: v.ownerId,
        primaryContactName: v.primaryContactName.trim() || null,
        primaryContactEmail: v.primaryContactEmail.trim() || null,
        primaryContactPhone: v.primaryContactPhone.trim() || null,
        estimatedValuePaise: estPaise,
        expectedCloseDate: v.expectedCloseDate || null,
        nextTouchDate: v.nextTouchDate || null,
        notes: v.notes.trim() || null,
        serviceOfferingIds: v.serviceOfferingIds,
      });
    },
    onSuccess: async () => {
      setError(null);
      await invalidate();
    },
    onError: (err) => setError(toMessage(err)),
  });

  const transitionMutation = useMutation({
    mutationFn: (payload: {
      toStage: ProspectStage;
      lostReason?: string;
      clientConversion?: ClientConversionPayload;
    }) => api.transitionProspect(prospectId, payload),
    onSuccess: async (res) => {
      if (res.status === 'dedupe_suggestions') {
        setConversionSuggestions(res.suggestions);
        return;
      }
      setConversionOpen(false);
      setLostOpen(false);
      setPendingStage(null);
      setConversionSuggestions([]);
      setError(null);
      await invalidate();
      if (res.convertedClientId) {
        await navigate({ to: '/crm/clients/$clientId', params: { clientId: res.convertedClientId } });
      }
    },
    onError: (err) => setError(toMessage(err)),
  });

  const correctForm = useForm<CorrectValues>({ defaultValues: { toStage: 'lead', reason: '' } });

  const correctMutation = useMutation({
    mutationFn: (v: CorrectValues) =>
      api.correctProspectStage(prospectId, { toStage: v.toStage, reason: v.reason.trim() }),
    onSuccess: async () => {
      setCorrectOpen(false);
      correctForm.reset();
      setError(null);
      await invalidate();
    },
    onError: (err) =>
      setError(toMessage(err)),
  });

  const activityForm = useForm<ActivityValues>({
    defaultValues: {
      kind: 'call',
      occurredAt: nowLocalIso(),
      durationMinutes: '',
      summary: '',
      outcome: '',
    },
  });

  const addActivityMutation = useMutation({
    mutationFn: (v: ActivityValues) =>
      api.addProspectActivity(prospectId, {
        kind: v.kind,
        occurredAt: new Date(v.occurredAt).toISOString(),
        durationMinutes: v.durationMinutes.trim() ? Number(v.durationMinutes) : undefined,
        summary: v.summary.trim(),
        outcome: v.outcome === '' ? undefined : v.outcome,
      }),
    onSuccess: async () => {
      activityForm.reset({
        kind: 'call',
        occurredAt: nowLocalIso(),
        durationMinutes: '',
        summary: '',
        outcome: '',
      });
      setError(null);
      await invalidate();
    },
    onError: (err) => setError(toMessage(err)),
  });

  const conversionForm = useForm<ConversionValues>({
    defaultValues: {
      legalName: '',
      gstin: '',
      gstTreatment: 'registered',
      pan: '',
      billingAddress: '',
      placeOfSupplyState: '',
      paymentTermsDays: 30,
      force: false,
    },
  });

  React.useEffect(() => {
    const d = detailQuery.data;
    if (d && conversionOpen) {
      conversionForm.reset({
        legalName: d.displayName,
        gstin: '',
        gstTreatment: 'registered',
        pan: '',
        billingAddress: '',
        placeOfSupplyState: '',
        paymentTermsDays: 30,
        force: false,
      });
    }
  }, [conversionOpen, detailQuery.data, conversionForm]);

  const lostForm = useForm<LostValues>({ defaultValues: { lostReason: '' } });

  const d = detailQuery.data;
  const activities = activitiesQuery.data ?? [];
  const people = peopleQuery.data ?? [];
  const services = servicesQuery.data ?? [];
  const activeServices = services.filter((s) => s.active || d?.interests.some((i) => i.id === s.id));

  if (detailQuery.isLoading || !d) {
    return (
      <AppShell>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading…</p>
      </AppShell>
    );
  }

  const nextStages = VALID_TRANSITIONS[d.stage];

  const handleStageClick = (toStage: ProspectStage): void => {
    if (toStage === 'won' && d.currentClientId === null) {
      setConversionOpen(true);
      return;
    }
    if (toStage === 'lost') {
      setLostOpen(true);
      return;
    }
    setPendingStage(toStage);
  };

  return (
    <AppShell>
      <div className="flex flex-col gap-2">
        <div className="text-xs text-[hsl(var(--muted-foreground))]">
          <Link to="/sales/prospects">← Prospects</Link>
        </div>
        <h1 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">
          {d.displayName}
        </h1>
        <div className="flex flex-wrap items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
          <Badge variant={stageBadgeVariant(d.stage)}>{stageLabel(d.stage)}</Badge>
          <Badge variant="muted">{d.status}</Badge>
          <span>· Owner {d.ownerName}</span>
          <span>· Channel {d.leadChannelName}</span>
          {d.convertedClientId ? (
            <Link
              to="/crm/clients/$clientId"
              params={{ clientId: d.convertedClientId }}
              className="text-[hsl(var(--brand))] hover:underline"
            >
              View converted client
            </Link>
          ) : null}
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Advance stage</CardTitle>
          <CardDescription>
            Current: {stageLabel(d.stage)}. Stages move forward only — a transition cannot be
            reversed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {nextStages.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Terminal stage. No further transitions.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {nextStages.map((s) => (
                <Button
                  key={s}
                  variant={s === 'lost' ? 'destructive' : 'default'}
                  onClick={() => handleStageClick(s)}
                  disabled={transitionMutation.isPending}
                >
                  → {stageLabel(s)}
                </Button>
              ))}
            </div>
          )}
          {isAdmin && d.stage !== 'won' ? (
            <button
              type="button"
              className="mt-4 text-xs text-[hsl(var(--muted-foreground))] underline underline-offset-2 hover:text-[hsl(var(--foreground))]"
              onClick={() => {
                correctForm.reset({ toStage: d.stage, reason: '' });
                setCorrectOpen(true);
              }}
            >
              Stage set by mistake? Correct it
            </button>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Prospect details</CardTitle>
          <CardDescription>
            Interests: {d.interests.length === 0 ? '—' : d.interests.map((i) => i.name).join(', ')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex flex-wrap gap-1">
            {d.interests.map((i) => (
              <Badge key={i.id} variant="secondary">
                {i.name}
              </Badge>
            ))}
          </div>
          <form
            onSubmit={editForm.handleSubmit((v) => updateMutation.mutate(v))}
            className="flex flex-col gap-3"
          >
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Display name">
                <Input {...editForm.register('displayName', { required: true })} />
              </Field>
              <Field label="Owner">
                <Select {...editForm.register('ownerId', { required: true })}>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Contact name">
                <Input {...editForm.register('primaryContactName')} />
              </Field>
              <Field label="Contact email">
                <Input type="email" {...editForm.register('primaryContactEmail')} />
              </Field>
              <Field label="Contact phone">
                <Input {...editForm.register('primaryContactPhone')} />
              </Field>
              <Field label="Estimated value (₹)">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  {...editForm.register('estimatedValueRupees')}
                />
              </Field>
              <Field label="Expected close date">
                <Input type="date" {...editForm.register('expectedCloseDate')} />
              </Field>
              <Field label="Next touch date">
                <Input type="date" {...editForm.register('nextTouchDate')} />
              </Field>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Service interests</Label>
              <div className="flex flex-wrap gap-2 rounded-xl border border-[hsl(var(--input))] px-3 py-2 text-sm">
                {activeServices.length === 0 ? (
                  <span className="text-[hsl(var(--muted-foreground))]">No services configured.</span>
                ) : (
                  activeServices.map((s) => (
                    <label key={s.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        value={s.id}
                        {...editForm.register('serviceOfferingIds')}
                      />
                      <span>{s.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <Field label="Notes">
              <Textarea rows={3} {...editForm.register('notes')} />
            </Field>
            <div className="flex justify-end">
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </form>
          {d.lostReason ? (
            <div className="mt-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))] p-3 text-xs">
              <div className="font-medium">Lost reason</div>
              <div>{d.lostReason}</div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Activity timeline</CardTitle>
          <CardDescription>Newest first. Log every interaction.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={activityForm.handleSubmit((v) => addActivityMutation.mutate(v))}
            className="mb-4 grid gap-3 md:grid-cols-6"
          >
            <div className="md:col-span-1">
              <Label>Kind</Label>
              <Select {...activityForm.register('kind')}>
                {ACTIVITY_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Occurred at</Label>
              <Input type="datetime-local" {...activityForm.register('occurredAt', { required: true })} />
            </div>
            <div className="md:col-span-1">
              <Label>Duration (min)</Label>
              <Input type="number" min="0" {...activityForm.register('durationMinutes')} />
            </div>
            <div className="md:col-span-1">
              <Label>Outcome</Label>
              <Select {...activityForm.register('outcome')}>
                <option value="">—</option>
                {OUTCOMES.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </Select>
            </div>
            <div className="md:col-span-1 flex items-end">
              <Button type="submit" disabled={addActivityMutation.isPending} className="w-full">
                Log
              </Button>
            </div>
            <div className="md:col-span-6">
              <Label>Summary</Label>
              <Textarea rows={2} {...activityForm.register('summary', { required: true })} />
            </div>
          </form>

          {activities.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No activities logged yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {activities.map((a) => (
                <li key={a.id} className="py-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                    <Badge variant="outline">{a.kind}</Badge>
                    <span>{new Date(a.occurredAt).toLocaleString('en-IN')}</span>
                    {a.durationMinutes !== null ? <span>· {a.durationMinutes} min</span> : null}
                    {a.outcome !== null ? <Badge variant="muted">{a.outcome}</Badge> : null}
                    <span>· by {a.performedByName}</span>
                  </div>
                  <div className="mt-1 whitespace-pre-wrap">{a.summary}</div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {d.estimatedValuePaise !== null ? (
        <div className="text-xs text-[hsl(var(--muted-foreground))]">
          Est. value {formatInr(paise(BigInt(d.estimatedValuePaise)))}
        </div>
      ) : null}

      <Dialog
        open={pendingStage !== null}
        onOpenChange={(open) => (open ? undefined : setPendingStage(null))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Move to {pendingStage ? stageLabel(pendingStage) : ''}?
            </DialogTitle>
            <DialogDescription>
              {d.displayName} moves from {stageLabel(d.stage)} to{' '}
              {pendingStage ? stageLabel(pendingStage) : ''}. Stages only move forward — this
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingStage(null)}>
              Cancel
            </Button>
            <Button
              disabled={transitionMutation.isPending}
              onClick={() => {
                if (pendingStage) transitionMutation.mutate({ toStage: pendingStage });
              }}
            >
              {transitionMutation.isPending
                ? 'Moving…'
                : `Move to ${pendingStage ? stageLabel(pendingStage) : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={correctOpen} onOpenChange={setCorrectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Correct stage</DialogTitle>
            <DialogDescription>
              Admin-only. This does not undo history — the correction is recorded in the audit log
              with your reason. Won and Lost must go through the normal transition.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={correctForm.handleSubmit((v) => correctMutation.mutate(v))}
            className="flex flex-col gap-3"
          >
            <Field label="Stage">
              <Select {...correctForm.register('toStage')}>
                {CORRECTABLE_STAGES.map((s) => (
                  <option key={s} value={s}>
                    {stageLabel(s)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Reason">
              <Textarea
                rows={3}
                placeholder="Why is the current stage wrong?"
                {...correctForm.register('reason', { required: true })}
              />
            </Field>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setCorrectOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={correctMutation.isPending}>
                {correctMutation.isPending ? 'Correcting…' : 'Correct stage'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={conversionOpen} onOpenChange={setConversionOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Convert to client (Won)</DialogTitle>
            <DialogDescription>
              A new client will be created from this prospect. GSTIN is checksum-validated.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={conversionForm.handleSubmit((v) =>
              transitionMutation.mutate({
                toStage: 'won',
                clientConversion: {
                  legalName: v.legalName.trim(),
                  gstin: v.gstin.trim() || undefined,
                  gstTreatment: v.gstTreatment,
                  pan: v.pan.trim() || undefined,
                  billingAddress: v.billingAddress.trim(),
                  placeOfSupplyState: v.placeOfSupplyState.trim(),
                  paymentTermsDays: Number(v.paymentTermsDays),
                  force: v.force,
                },
              }),
            )}
            className="flex flex-col gap-3"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Legal name">
                <Input {...conversionForm.register('legalName', { required: true })} />
              </Field>
              <Field label="GSTIN">
                <Input {...conversionForm.register('gstin')} />
              </Field>
              <Field label="PAN">
                <Input {...conversionForm.register('pan')} />
              </Field>
              <Field label="GST treatment">
                <Select {...conversionForm.register('gstTreatment')}>
                  <option value="registered">Registered</option>
                  <option value="unregistered">Unregistered</option>
                  <option value="composition">Composition</option>
                  <option value="exempt">Exempt</option>
                </Select>
              </Field>
              <Field label="Place of supply (state)">
                <Input {...conversionForm.register('placeOfSupplyState', { required: true })} />
              </Field>
              <Field label="Payment terms (days)">
                <Input
                  type="number"
                  {...conversionForm.register('paymentTermsDays', { valueAsNumber: true })}
                />
              </Field>
            </div>
            <Field label="Billing address">
              <Textarea rows={3} {...conversionForm.register('billingAddress', { required: true })} />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" {...conversionForm.register('force')} />
              Create even if duplicates are found
            </label>
            {conversionSuggestions.length > 0 ? (
              <Alert variant="info">
                <AlertTitle>Possible duplicate clients</AlertTitle>
                <AlertDescription>
                  <ul className="mt-2 flex flex-col gap-1 text-xs">
                    {conversionSuggestions.map((s) => (
                      <li key={s.id}>
                        {s.legalName} · {s.reason} · GSTIN {s.gstin ?? '—'}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs">
                    Tick “Create even if duplicates are found” to convert anyway.
                  </p>
                </AlertDescription>
              </Alert>
            ) : null}
            <DialogFooter>
              <Button type="submit" disabled={transitionMutation.isPending}>
                {transitionMutation.isPending ? 'Converting…' : 'Convert & mark Won'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={lostOpen} onOpenChange={setLostOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as Lost</DialogTitle>
            <DialogDescription>Capture a reason. Prospect can be resurfaced later.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={lostForm.handleSubmit((v) =>
              transitionMutation.mutate({ toStage: 'lost', lostReason: v.lostReason.trim() || undefined }),
            )}
            className="flex flex-col gap-3"
          >
            <Field label="Reason">
              <Textarea rows={3} {...lostForm.register('lostReason')} />
            </Field>
            <DialogFooter>
              <Button type="submit" variant="destructive" disabled={transitionMutation.isPending}>
                {transitionMutation.isPending ? 'Saving…' : 'Mark Lost'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
